import { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Box,
  Tabs,
  Center,
  Loader,
  Button,
  Text,
  Stack,
  Group,
  Paper,
  Divider,
} from '@mantine/core';
import { useMediaQuery } from '@mantine/hooks';
import {
  IconTrendingUp,
  IconUsers,
  IconArrowRight,
} from '@tabler/icons-react';
import { useInfiniteQuery, useQueryClient } from '@tanstack/react-query';
import { useInfiniteScroll } from '../../hooks/useInfiniteScroll';
import { useAuth } from '../../context/AuthContext';
import { feedService } from '../../services/feed';
import { ReviewCard } from '../../components/reviews';
import { SuggestedUsers } from '../../components/sidebar/SuggestedUsers';
import { OnboardingModal } from '../../components/common/OnboardingModal';
import type { Review } from '../../types';

function FeedSkeleton() {
  return (
    <>
      {Array.from({ length: 5 }).map((_, i) => (
        <Box
          key={i}
          style={{
            padding: '0.75rem 1rem',
            borderBottom: '1px solid var(--mantine-color-default-border)',
          }}
        >
          <Group align="flex-start" gap="xs" wrap="nowrap">
            <Box
              style={{
                width: 32,
                height: 32,
                flexShrink: 0,
                borderRadius: '50%',
                backgroundColor: 'var(--mantine-color-default-hover)',
              }}
            />
            <Stack gap={6} style={{ flex: 1 }}>
              <Box style={{ height: 12, width: '30%', borderRadius: 4, backgroundColor: 'var(--mantine-color-default-hover)' }} />
              <Box style={{ height: 10, width: '50%', borderRadius: 4, backgroundColor: 'var(--mantine-color-default-hover)' }} />
              <Box style={{ height: 10, borderRadius: 4, backgroundColor: 'var(--mantine-color-default-hover)' }} />
              <Box style={{ height: 10, width: '80%', borderRadius: 4, backgroundColor: 'var(--mantine-color-default-hover)' }} />
            </Stack>
          </Group>
        </Box>
      ))}
    </>
  );
}

export default function Home() {
  const { isAuthenticated, isLoading: authLoading, user } = useAuth();
  const isMobile = !useMediaQuery('(min-width: 576px)');
  const [onboardingOpen, setOnboardingOpen] = useState(
    () => !!user && user.hasCompletedOnboarding === false
  );
  const [activeTab, setActiveTab] = useState<string | null>('discover');
  const queryClient = useQueryClient();

  const {
    data: discoverData,
    isLoading: isLoadingDiscover,
    error: discoverError,
    fetchNextPage: fetchMoreDiscover,
    hasNextPage: discoverHasMore,
    isFetchingNextPage: isFetchingMoreDiscover,
    refetch: refetchDiscover,
  } = useInfiniteQuery({
    queryKey: ['feed', 'discover'],
    queryFn: ({ pageParam }) => feedService.getDiscoverFeed(pageParam, 10),
    initialPageParam: 1,
    getNextPageParam: (lastPage) => lastPage.hasNextPage ? lastPage.page + 1 : undefined,
    enabled: !authLoading,
    staleTime: 60_000,
  });

  const {
    data: followingData,
    isLoading: isLoadingFollowing,
    error: followingError,
    fetchNextPage: fetchMoreFollowing,
    hasNextPage: followingHasMore,
    isFetchingNextPage: isFetchingMoreFollowing,
    refetch: refetchFollowing,
  } = useInfiniteQuery({
    queryKey: ['feed', 'following'],
    queryFn: ({ pageParam }) => feedService.getFollowingFeed(pageParam, 10),
    initialPageParam: 1,
    getNextPageParam: (lastPage) => lastPage.hasNextPage ? lastPage.page + 1 : undefined,
    enabled: !authLoading && isAuthenticated && activeTab === 'following',
    staleTime: 60_000,
  });

  const discoverReviews = discoverData?.pages.flatMap((p) => p.items) ?? [];
  const followingReviews = followingData?.pages.flatMap((p) => p.items) ?? [];

  const discoverSentinel = useInfiniteScroll(fetchMoreDiscover, !!discoverHasMore, isFetchingMoreDiscover);
  const followingSentinel = useInfiniteScroll(fetchMoreFollowing, !!followingHasMore, isFetchingMoreFollowing);

  const handleHelpfulChange = (reviewId: string, helpfulCount: number) => {
    const patchPages = (old: any) => old ? {
      ...old,
      pages: old.pages.map((page: any) => ({
        ...page,
        items: page.items.map((r: Review) => r.id === reviewId ? { ...r, helpfulCount } : r),
      })),
    } : old;
    queryClient.setQueryData(['feed', 'discover'], patchPages);
    queryClient.setQueryData(['feed', 'following'], patchPages);
  };

  const handleDelete = (reviewId: string) => {
    const patchPages = (old: any) => old ? {
      ...old,
      pages: old.pages.map((page: any) => ({
        ...page,
        items: page.items.filter((r: Review) => r.id !== reviewId),
      })),
    } : old;
    queryClient.setQueryData(['feed', 'discover'], patchPages);
    queryClient.setQueryData(['feed', 'following'], patchPages);
  };

  const renderFeed = (
    reviews: Review[],
    isLoading: boolean,
    error: unknown,
    isFetchingMore: boolean,
    sentinelRef: React.RefObject<HTMLDivElement | null>,
    onRetry: () => void,
    tab: 'discover' | 'following' = 'discover',
  ) => {
    if (error) {
      return (
        <Center py="xl">
          <Stack align="center" gap="xs">
            <Text c="dimmed">Failed to load reviews.</Text>
            <Button variant="subtle" size="xs" onClick={onRetry}>Try again</Button>
          </Stack>
        </Center>
      );
    }

    if (!isLoading && reviews.length === 0) {
      if (tab === 'following') {
        return (
          <Stack gap="lg" py="xl" px="md">
            <Stack align="center" gap="xs">
              <IconUsers size={36} color="var(--mantine-color-dimmed)" />
              <Text fw={600}>Your Following feed is empty</Text>
              <Text c="dimmed" size="sm" ta="center">
                Follow some reviewers to see their latest reviews here.
              </Text>
            </Stack>
            <SuggestedUsers />
          </Stack>
        );
      }
      return (
        <Center py={60}>
          <Stack align="center" gap="xs">
            <Text c="dimmed" size="sm">No reviews yet. Be the first to share!</Text>
            <Button
              component={Link}
              to="/reviews/create"
              size="xs"
              radius="xl"
              variant="subtle"
              rightSection={<IconArrowRight size={14} />}
            >
              Write a review
            </Button>
          </Stack>
        </Center>
      );
    }

    return (
      <>
        {reviews.map((review) => (
          <ReviewCard
            key={review.id}
            review={review}
            showProduct
            variant="list"
            onHelpfulChange={handleHelpfulChange}
            onDelete={handleDelete}
          />
        ))}
        {(isLoading || isFetchingMore) && <FeedSkeleton />}
        <div ref={sentinelRef} />
        {/* Suggested users strip — shown inline on mobile where sidebar is hidden */}
        {isMobile && tab === 'discover' && !isLoading && reviews.length > 0 && (
          <Paper p="md" style={{ borderTop: '1px solid var(--mantine-color-default-border)' }}>
            <Divider label="Suggested for you" labelPosition="left" mb="sm" />
            <SuggestedUsers />
          </Paper>
        )}
      </>
    );
  };

  if (authLoading) {
    return <Center h={400}><Loader size="lg" /></Center>;
  }

  return (
    <Box>
      {/* Sticky tab bar */}
      <Box
        style={{
          position: 'sticky',
          top: 0,
          zIndex: 10,
          backgroundColor: 'light-dark(var(--mantine-color-white), var(--mantine-color-dark-7))',
          borderBottom: '1px solid var(--mantine-color-default-border)',
        }}
      >
        <Tabs
          value={activeTab}
          onChange={setActiveTab}
          variant="unstyled"
          styles={{
            list: { display: 'flex' },
            tab: {
              flex: 1,
              borderRadius: 0,
              padding: '0.875rem 0',
              fontWeight: 600,
              color: 'var(--mantine-color-dimmed)',
              borderBottom: '2px solid transparent',
              transition: 'color 120ms, border-color 120ms',
            },
          }}
        >
          <Tabs.List>
            <Tabs.Tab
              value="discover"
              leftSection={<IconTrendingUp size={15} />}
              style={activeTab === 'discover' ? {
                color: 'var(--mantine-color-text)',
                borderBottomColor: 'var(--mantine-color-brand-6)',
              } : undefined}
            >
              Discover
            </Tabs.Tab>
            {isAuthenticated && (
              <Tabs.Tab
                value="following"
                leftSection={<IconUsers size={15} />}
                style={activeTab === 'following' ? {
                  color: 'var(--mantine-color-text)',
                  borderBottomColor: 'var(--mantine-color-brand-6)',
                } : undefined}
              >
                Following
              </Tabs.Tab>
            )}
          </Tabs.List>
        </Tabs>
      </Box>

      {/* Feed */}
      <Tabs value={activeTab}>
        <Tabs.Panel value="discover">
          {renderFeed(discoverReviews, isLoadingDiscover, discoverError, isFetchingMoreDiscover, discoverSentinel, () => refetchDiscover(), 'discover')}
        </Tabs.Panel>
        {isAuthenticated && (
          <Tabs.Panel value="following">
            {renderFeed(followingReviews, isLoadingFollowing, followingError, isFetchingMoreFollowing, followingSentinel, () => refetchFollowing(), 'following')}
          </Tabs.Panel>
        )}
      </Tabs>

      <OnboardingModal
        opened={onboardingOpen}
        onClose={() => setOnboardingOpen(false)}
      />
    </Box>
  );
}
